import { useCallback, useMemo } from 'react';
import { BeraResultCycleSummary } from '@/types/Bera';

type HighlightParams = {
	type: 'step' | 'task-total';
	taskIndex: number;
	scoreIndex: number;
	value: number;
};

const HIGH_SCORE_THRESHOLD = 15;

const collectStepCells = (cycle: BeraResultCycleSummary) =>
	cycle.task_rpn_scores.flatMap((task, taskIndex) =>
		task.rpn_scores.map((score, scoreIndex) => ({
			taskIndex,
			scoreIndex,
			value: score.task_rpn
		}))
	);

const makeStepKey = (taskIndex: number, scoreIndex: number): string => `step-${taskIndex}-${scoreIndex}`;

export function useNotENS(cycleSummary: BeraResultCycleSummary) {
	const top5StepKeys = useMemo(() => {
		const cells = collectStepCells(cycleSummary);

		const top5 = cells
			.filter((c) => c.value > 0)
			.sort((a, b) => b.value - a.value)
			.slice(0, 5);

		return new Set(top5.map((c) => makeStepKey(c.taskIndex, c.scoreIndex)));
	}, [cycleSummary]);

	const topTaskTotalIndex = useMemo(() => {
		const totals = cycleSummary.task_rpn_scores
			.map((task, index) => ({ index, value: task.rpn }))
			.filter((t) => t.value > 0)
			.sort((a, b) => b.value - a.value);

		return totals[0]?.index ?? -1;
	}, [cycleSummary]);

	return useCallback(
		(params: HighlightParams): string => {
			if (params.value <= 0) return 'black';
			if (params.value <= HIGH_SCORE_THRESHOLD) return 'black';

			if (params.type === 'task-total') {
				return params.taskIndex === topTaskTotalIndex ? 'red' : 'black';
			}
			const key = makeStepKey(params.taskIndex, params.scoreIndex);
			return top5StepKeys.has(key) ? 'red' : 'black';
		},
		[top5StepKeys, topTaskTotalIndex]
	);
}
